<template lang="pug">
el-dialog(
    :title="$t('Add content element')"
    :visible.sync="localVisible"
    :close-on-press-escape="true"
    :close-on-click-modal="true"
    :append-to-body="true"
    custom-class="add-section-dialog"
    top="48px"
    width="860px"
    @closed="$emit('closed')"
)
    .dialog-body(v-if="hasOptions")
        .tab-sidebar
            button.tab-btn(
                v-if="popularOptions.length"
                :class="{ active: activeTab === '__popular' }"
                type="button"
                @click="setTab('__popular')"
            )
                i.fas.el-icon-fa-star.tab-btn-icon
                span {{ $t('Beliebt') }}
            template(v-for="group in groupedOptions")
                .tab-divider
                button.tab-btn(
                    :key="group.key"
                    :class="{ active: activeTab === group.key }"
                    type="button"
                    @click="setTab(group.key)"
                )
                    i.tab-btn-icon(:class="group.icon")
                    span {{ group.label }}
        .tab-content
            .content-toolbar
                el-input(
                    ref="searchInput"
                    v-model="searchQuery"
                    clearable
                    prefix-icon="el-icon-search"
                    :placeholder="$t('Search...')"
                    size="small"
                    @input="onSearch"
                    @clear="onClear"
                )
                .drag-hint
                    i.fas.el-icon-fa-hand-pointer
                    span {{ $t('section.add.hint') }}
            template(v-if="searchQuery")
                .section-grid(v-if="searchResults.length")
                    button.section-card(
                        v-for="section in searchResults"
                        :key="section.key"
                        type="button"
                        draggable="true"
                        :class="{ 'is-disabled': section.disabled, 'not-permitted': section.notPermitted, 'is-grid': section.is === 'grid' }"
                        :disabled="section.disabled"
                        @click="select(section)"
                        @dragstart="onDragStart(section, $event)"
                    )
                        .section-card-icon
                            span.section-card-svg(v-if="section.is === 'grid'" v-html="section.svgIcon")
                            i(v-else :class="section.icon || 'fas el-icon-fa-puzzle-piece'")
                        .section-card-body
                            .section-card-name {{ section.name }}
                            .section-card-desc(v-if="section.description") {{ section.description }}
                        .section-card-badge(v-if="section.notPermitted")
                            i.fas.el-icon-fa-lock
                .empty-search(v-else)
                    i.fas.el-icon-fa-meh
                    p {{ $t('section.search.no_results', { query: searchQuery }) }}
            template(v-else)
                .popular-pane(v-if="activeTab === '__popular'")
                    template(v-if="recentOptions.length")
                        .pane-subhead.recent-subhead
                            i.fas.el-icon-fa-history
                            span {{ $t('Zuletzt verwendet') }}
                        .card-grid
                            button.section-card.is-recent(
                                v-for="section in recentOptions"
                                :key="'recent-' + section.key"
                                type="button"
                                draggable="true"
                                :class="{ 'is-disabled': section.disabled, 'not-permitted': section.notPermitted, 'is-grid': section.is === 'grid' }"
                                :disabled="section.disabled"
                                @click="select(section)"
                                @dragstart="onDragStart(section, $event)"
                            )
                                .section-card-icon
                                    span.section-card-svg(v-if="section.is === 'grid'" v-html="section.svgIcon")
                                    i(v-else :class="section.icon || 'fas el-icon-fa-puzzle-piece'")
                                .section-card-body
                                    .section-card-name {{ section.name }}
                                    .section-card-desc(v-if="section.description") {{ section.description }}
                                .section-card-badge(v-if="section.notPermitted")
                                    i.fas.el-icon-fa-lock
                        .pane-subhead
                            i.fas.el-icon-fa-star
                            span {{ $t('Beliebt') }}
                    .card-grid
                        button.section-card(
                            v-for="section in popularOptions"
                            :key="section.key"
                            type="button"
                            draggable="true"
                            :class="{ 'is-disabled': section.disabled, 'not-permitted': section.notPermitted }"
                            :disabled="section.disabled"
                            @click="select(section)"
                            @dragstart="onDragStart(section, $event)"
                        )
                            .section-card-icon
                                span.section-card-svg(v-if="section.is === 'grid'" v-html="section.svgIcon")
                                i(v-else :class="section.icon || 'fas el-icon-fa-puzzle-piece'")
                            .section-card-body
                                .section-card-name {{ section.name }}
                                .section-card-desc(v-if="section.description") {{ section.description }}
                            .section-card-badge(v-if="section.notPermitted")
                                i.fas.el-icon-fa-lock
                template(v-for="group in groupedOptions")
                    .section-grid(v-if="activeTab === group.key" :key="group.key")
                        button.section-card(
                            v-for="section in group.items"
                            :key="section.key"
                            type="button"
                            draggable="true"
                            :class="{ 'is-disabled': section.disabled, 'not-permitted': section.notPermitted, 'is-grid': section.is === 'grid' }"
                            :disabled="section.disabled"
                            @click="select(section)"
                            @dragstart="onDragStart(section, $event)"
                        )
                            .section-card-icon
                                span.section-card-svg(v-if="section.is === 'grid'" v-html="section.svgIcon")
                                i(v-else :class="section.icon || 'fas el-icon-fa-puzzle-piece'")
                            .section-card-body
                                .section-card-name {{ section.name }}
                                .section-card-desc(v-if="section.description") {{ section.description }}
                            .section-card-badge(v-if="section.notPermitted")
                                i.fas.el-icon-fa-lock
    .empty-state(v-else)
        p {{ $t('No content elements available.') }}
</template>

<i18n>
{
    "de": {
        "Add content element": "Inhaltselement hinzufügen",
        "Beliebt": "Beliebt",
        "Zuletzt verwendet": "Zuletzt verwendet",
        "Texte": "Texte",
        "Medien": "Medien",
        "Navigation & Aktionen": "Navigation & Aktionen",
        "Listen & Übersichten": "Listen & Übersichten",
        "Social Media": "Social Media",
        "Shop": "Shop",
        "Newsletter": "Newsletter",
        "Sonstiges": "Sonstiges",
        "Layout": "Layout",
        "No content elements available.": "Keine Inhaltselemente verfügbar.",
        "Search...": "Suchen..."
    },
    "en": {
        "Add content element": "Add content element",
        "Beliebt": "Popular",
        "Zuletzt verwendet": "Recently used",
        "Texte": "Texts",
        "Medien": "Media",
        "Navigation & Aktionen": "Navigation & Actions",
        "Listen & Übersichten": "Lists & Overviews",
        "Social Media": "Social Media",
        "Shop": "Shop",
        "Newsletter": "Newsletter",
        "Sonstiges": "Miscellaneous",
        "Layout": "Layout",
        "No content elements available.": "No content elements available.",
        "Search...": "Search..."
    }
}
</i18n>

<script>
import _ from 'lodash'
import { useAuthStore, useSetupStore } from '@/stores'

// Per-user store key for the "Zuletzt verwendet" list, persisted through the
// generic settings.misc key-value store (no backend schema change).
const RECENT_MISC_KEY = 'add-section:recent'
const RECENT_MAX = 4

// viewBox 24x16, usable area 22x14 (1px padding), gap=2px
// 1col: w=22  |  2col: w=10  |  3col: w=6  |  4col: w=4  — all integers
const GRID_SVG_BY_COLS = {
    1: `<svg width="24" height="16" viewBox="0 0 24 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="22" height="14" rx="2"/></svg>`,
    2: `<svg width="24" height="16" viewBox="0 0 24 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="10" height="14" rx="2"/><rect x="13" y="1" width="10" height="14" rx="2"/></svg>`,
    3: `<svg width="24" height="16" viewBox="0 0 24 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="6" height="14" rx="2"/><rect x="9" y="1" width="6" height="14" rx="2"/><rect x="17" y="1" width="6" height="14" rx="2"/></svg>`,
    4: `<svg width="24" height="16" viewBox="0 0 24 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="4" height="14" rx="2"/><rect x="7" y="1" width="4" height="14" rx="2"/><rect x="13" y="1" width="4" height="14" rx="2"/><rect x="19" y="1" width="4" height="14" rx="2"/></svg>`,
}

const GROUP_ORDER = [
    { key: 'Texte',                 icon: 'fas el-icon-fa-font'          },
    { key: 'Medien',                icon: 'fas el-icon-fa-images'        },
    { key: 'Navigation & Aktionen', icon: 'fas el-icon-fa-hand-pointer'  },
    { key: 'Listen & Übersichten',  icon: 'fas el-icon-fa-list'          },
    { key: 'Social Media',          icon: 'fab el-icon-fa-instagram'     },
    { key: 'Shop',                  icon: 'fas el-icon-fa-shopping-cart' },
    { key: 'Newsletter',            icon: 'fas el-icon-fa-envelope'      },
    { key: 'Sonstiges',             icon: 'fas el-icon-fa-toolbox'       },
    { key: '__layout',              icon: 'fas el-icon-fa-table'         },
    { key: '__unknown',             icon: 'fas el-icon-fa-question-circle' },
]


export default {
    name: 'AddSectionDialog',
    props: {
        visible:        { type: Boolean, default: false },
        sectionOptions: { type: Array,   default: () => [] },
        gridOptions:    { type: Array,   default: () => [] },
    },
    data() {
        return {
            activeTab: '__popular',
            lastTab: '__popular',
            searchQuery: '',
            recentKeys: [],
        }
    },
    computed: {
        localVisible: {
            get() { return this.visible },
            set(val) { this.$emit('update:visible', val) },
        },
        allOptions() {
            const toArray = (v) => {
                if (!v) return null
                return Array.isArray(v) ? v : [v]
            }
            const normalizeIcon = (icon) => {
                if (!icon) return null
                if (icon.includes('el-icon-fa-')) return icon
                // convert FA6 format (fa-solid fa-xxx) → project format (fas el-icon-fa-xxx)
                const map = { 'fa-solid': 'fas', 'fa-regular': 'far', 'fa-brands': 'fab', 'fa-light': 'fal', 'fa-thin': 'fat', 'fa-duotone': 'fad' }
                const parts = icon.split(' ')
                const weight = map[parts[0]]
                const name = parts[1] ? parts[1].replace('fa-', 'el-icon-fa-') : null
                return weight && name ? `${weight} ${name}` : icon
            }
            const enrich = (s) => {
                const groups = toArray(s.groups || s.group) || ['__unknown']
                return {
                    ...s,
                    is:          s.is || 'section',
                    groups,
                    description: s.description || null,
                    popular:     s.popular === true || s.popular === 'true',
                    icon:        normalizeIcon(s.icon) || null,
                }
            }
            return [
                ...this.sectionOptions.map(enrich).filter(s => !s.disabled),
                ...this.gridOptions.map(g => {
                    const key = String(g.key || '')
                    const colPart = key.includes(':') ? key.split(':')[1] : key
                    const cols = colPart ? colPart.split('-').length : 1
                    const svgIcon = GRID_SVG_BY_COLS[cols] || GRID_SVG_BY_COLS[1]
                    return { ...enrich(g), is: 'grid', groups: ['__layout'], svgIcon }
                }).filter(s => !s.disabled),
            ]
        },
        hasOptions() {
            return this.allOptions.length > 0
        },
        popularOptions() {
            return this.allOptions.filter(s => s.popular === true || s.popular === 'true')
        },
        recentOptions() {
            // Map the stored keys (most recent first) back onto the currently
            // available options, dropping any that no longer exist, capped at RECENT_MAX.
            const byKey = {}
            this.allOptions.forEach(option => { byKey[option.key] = option })
            return this.recentKeys
                .map(key => byKey[key])
                .filter(Boolean)
                .slice(0, RECENT_MAX)
        },
        groupedOptions() {
            const grouped = {}
            this.allOptions.forEach(s => {
                const keys = s.groups && s.groups.length ? s.groups : ['__unknown']
                keys.forEach(key => {
                    if (!grouped[key]) grouped[key] = []
                    grouped[key].push(s)
                })
            })

            return GROUP_ORDER
                .filter(g => grouped[g.key])
                .map(g => ({
                    key:   g.key,
                    icon:  g.icon,
                    label: g.key === '__layout'  ? this.$t('Layout')
                         : g.key === '__unknown' ? this.$t('Sonstiges')
                         : this.$t(g.key),
                    items: grouped[g.key],
                }))
        },
        searchResults() {
            if (!this.searchQuery) return []
            const q = this.searchQuery.toLowerCase()
            return this.allOptions.filter(s =>
                s.name.toLowerCase().includes(q) ||
                (s.description && s.description.toLowerCase().includes(q)) ||
                (s.keywords && s.keywords.some(k => k.toLowerCase().includes(q)))
            )
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.searchQuery = ''
                this.loadRecent()
                this.setInitialTab()
                this.$nextTick(() => {
                    this.$refs.searchInput && this.$refs.searchInput.focus()
                })
            }
        },
    },
    methods: {
        loadRecent() {
            const misc = _.get(useAuthStore().user, 'settings.misc') || []
            const setting = misc.find(item => item && item.key === RECENT_MISC_KEY)
            const stored = _.get(setting, 'value')
            let keys = []
            if (Array.isArray(stored)) keys = stored
            else if (typeof stored === 'string' && stored) {
                try { keys = JSON.parse(stored) } catch (error) { keys = [] }
            }
            this.recentKeys = Array.isArray(keys) ? keys.filter(key => typeof key === 'string') : []
        },
        recordRecent(section) {
            if (!section || !section.key) return
            // Move this key to the front, drop duplicates, keep a small buffer so
            // items removed from the CMS don't shrink the visible list below RECENT_MAX.
            const keys = [section.key, ...this.recentKeys.filter(key => key !== section.key)].slice(0, RECENT_MAX * 3)
            this.recentKeys = keys
            useSetupStore().saveMiscSettings({ key: RECENT_MISC_KEY, value: JSON.stringify(keys) })
        },
        setInitialTab() {
            if (this.popularOptions.length) {
                this.activeTab = '__popular'
                this.lastTab = '__popular'
            } else if (this.groupedOptions.length) {
                this.activeTab = this.groupedOptions[0].key
                this.lastTab = this.groupedOptions[0].key
            }
        },
        setTab(key) {
            this.activeTab = key
            this.lastTab = key
            this.searchQuery = ''
        },
        onSearch() {
            if (!this.searchQuery) {
                this.activeTab = this.lastTab
            }
        },
        onClear() {
            this.searchQuery = ''
            this.activeTab = this.lastTab
        },
        onDragStart(section, event) {
            event.dataTransfer.effectAllowed = 'copy'
            event.dataTransfer.setData('text/plain', section.key)
            this.recordRecent(section)
            this.$emit('drag-start', section)
            setTimeout(() => { this.localVisible = false }, 0)
        },
        select(section) {
            this.recordRecent(section)
            this.$emit('select', section)
            this.localVisible = false
        },
    },
}
</script>

<style lang="sass" scoped>
:deep(.add-section-dialog)
    height: calc(100vh - 96px)
    max-height: calc(100vh - 96px)
    max-width: calc(100vw - 32px)
    margin: 48px auto !important
    display: flex
    flex-direction: column
    border-radius: 10px
    overflow: hidden
    .el-dialog__header
        padding: 16px 20px
        border-bottom: 1px solid #ebeef5
        background: #fff
    .el-dialog__title
        font-size: 16px
        font-weight: 600
        color: #1a1a2e
    .el-dialog__headerbtn
        top: 14px
    .el-dialog__body
        flex: 1
        min-height: 0
        overflow: hidden
        padding: 0

@media (min-height: 820px)
    :deep(.add-section-dialog)
        height: calc(100vh - 160px)
        max-height: calc(100vh - 160px)
        margin: 80px auto !important

.dialog-body
    display: flex
    height: 100%
    min-height: 0
    background: #f8f9fb

// ── Left sidebar ──────────────────────────────────────────────────────────────
.tab-sidebar
    width: 232px
    flex-shrink: 0
    border-right: 1px solid #e8eaed
    background: #f2f4f7
    padding: 0.6rem 0.5rem
    overflow-y: auto
    display: flex
    flex-direction: column
    gap: 2px

.tab-divider
    height: 1px
    background: #e0e3e8
    margin: 0.3rem 0.25rem

.tab-btn
    display: flex
    align-items: center
    gap: 0.6rem
    width: 100%
    padding: 0.5rem 0.75rem
    border: none
    background: transparent
    cursor: pointer
    font-size: 14px
    color: #5a6275
    text-align: left
    transition: background 0.12s, color 0.12s
    border-radius: 6px

    &:hover
        background: #e6e9ef
        color: #303133

    &.active
        background: #fff
        color: #409eff
        font-weight: 600
        box-shadow: 0 1px 4px rgba(0,0,0,0.08)

.tab-btn-icon
    width: 15px
    text-align: center
    flex-shrink: 0
    font-size: 12px
    opacity: 0.6

    .tab-btn.active &
        opacity: 1

// ── Content area ──────────────────────────────────────────────────────────────
.tab-content
    flex: 1
    min-width: 0
    display: flex
    flex-direction: column
    overflow: hidden
    background: #fff

.content-toolbar
    flex-shrink: 0
    padding: 1rem 1.25rem 0.65rem
    border-bottom: 1px solid #ebeef5
    background: #fff

    :deep(.el-input__inner)
        background: #fff
        border-color: #dcdfe6
        border-radius: 6px
        &:focus
            border-color: #409eff

.drag-hint
    display: flex
    align-items: center
    gap: 0.4rem
    margin-top: 0.55rem
    font-size: 12px
    color: #c0c8d4
    letter-spacing: 0.01em
    i
        font-size: 10px

.section-grid
    flex: 1
    overflow-y: auto
    padding: 1.25rem
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))
    gap: 0.65rem
    align-content: start
    background: #fff

// ── Popular tab: "Zuletzt verwendet" + "Beliebt" stacked in one scroll pane ────
.popular-pane
    flex: 1
    overflow-y: auto
    padding: 1.25rem
    background: #fff

.card-grid
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))
    gap: 0.65rem
    align-content: start

.pane-subhead
    display: flex
    align-items: center
    gap: 0.45rem
    font-size: 12px
    font-weight: 600
    text-transform: uppercase
    letter-spacing: 0.03em
    color: #a0a8b8
    margin: 0 0 0.75rem

    .card-grid + &
        margin-top: 1.5rem

    i
        font-size: 11px
        opacity: 0.7

    &.recent-subhead
        color: #409eff
        i
            opacity: 1

.empty-search
    padding: 3rem 1rem
    text-align: center
    color: #b0b8c4
    i
        font-size: 2.2rem
        margin-bottom: 0.85rem
        display: block
        opacity: 0.5
    p
        font-size: 13px

// ── Section cards ─────────────────────────────────────────────────────────────
button.section-card
    display: flex
    align-items: flex-start
    gap: 0.75rem
    padding: 0.8rem 1rem
    border: 1px solid #e8eaed
    border-radius: 8px
    background: #fff
    cursor: pointer
    text-align: left
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s, transform 0.1s
    position: relative
    width: 100%
    box-shadow: 0 1px 3px rgba(0,0,0,0.04)

    &:hover:not(:disabled)
        border-color: #409eff
        box-shadow: 0 3px 10px rgba(64, 158, 255, 0.14)
        background: #fafcff
        transform: translateY(-1px)

    &:active:not(:disabled)
        transform: translateY(0)
        box-shadow: 0 1px 4px rgba(64, 158, 255, 0.1)

    &.is-disabled
        opacity: 0.45
        cursor: not-allowed

    &.is-grid
        border-style: dashed
        border-color: #d0d4db
        background: #fafafa
        &:hover:not(:disabled)
            border-style: dashed
            border-color: #409eff
            background: #fafcff

    // "Zuletzt verwendet" cards: slightly different, Element light-blue tint
    &.is-recent
        background: #ecf5ff
        border-color: #d9ecff
        .section-card-icon
            background: #d9ecff
            color: #409eff
        &:hover:not(:disabled)
            border-color: #409eff
            background: #e3f0ff

.section-card-icon
    flex-shrink: 0
    width: 38px
    height: 38px
    display: flex
    align-items: center
    justify-content: center
    background: #f0f3f8
    border-radius: 8px
    font-size: 15px
    color: #7a8499
    transition: background 0.15s, color 0.15s

    button.section-card:hover:not(:disabled) &
        background: #ddeeff
        color: #409eff

.section-card-svg
    display: flex
    align-items: center
    justify-content: center
    width: 100%
    height: 100%
    :deep(svg)
        display: block

    button.section-card.is-grid &
        background: #ededf2
        color: #909399

.section-card-body
    flex: 1
    min-width: 0
    padding-top: 1px

.section-card-name
    font-size: 14px
    font-weight: 600
    color: #2c3044
    line-height: 1.3
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

.section-card-desc
    font-size: 12px
    color: #a0a8b8
    margin-top: 4px
    line-height: 1.45
    display: -webkit-box
    -webkit-line-clamp: 2
    -webkit-box-orient: vertical
    overflow: hidden

.section-card-badge
    position: absolute
    top: 7px
    right: 9px
    font-size: 11px
    color: #c8cdd6

.empty-state
    padding: 2rem
    text-align: center
    color: #b0b8c4
</style>
